import { Dispatch, useEffect, useRef, useState } from "react";
import { Form, FormControlProps } from "react-bootstrap";
import { valueFix } from "../utility";

/**
 * Props for the ControlBase component.
 */
export interface ControlBaseProps extends FormControlProps {
  value: string;
  setValue?: Dispatch<string>;
  whiteSpaceFix?: boolean;
  disabled?: boolean;
  type?: "string" | "textarea" | "number";
  forcePrefix?: string;
  className?: string;
  spellCheck?: boolean;
  debounceMilliseconds?: number;
  placeholder?: string;
  autoFocus?: boolean;
  rows?: number;
  allowCarriageReturn?: boolean;
}

/**
 * A basic input control component that provides common input functionality with customizable types and styling.
 *
 * @param props - Component props.
 * @param props.allowCarriageReturn - Whether to allow carriage returns in textarea (defaults to false)
 * @param props.autoFocus - Whether the input should automatically receive focus
 * @param props.className - Additional CSS class names to apply (defaults to "")
 * @param props.debounceMilliseconds - Debounce delay in milliseconds, 0 for immediate updates (defaults to 500)
 * @param props.disabled - Whether the control is disabled (defaults to false)
 * @param props.forcePrefix - A prefix that must be present at the start of the value (defaults to "")
 * @param props.placeholder - Placeholder text to display when input is empty
 * @param props.rows - Number of rows for textarea type
 * @param props.setValue - Callback function to update the value when changed
 * @param props.spellCheck - Whether spell checking is enabled (defaults to true)
 * @param props.type - The type of input control to render (defaults to "string")
 * @param props.value - The current value of the input control
 * @param props.whiteSpaceFix - Whether to automatically trim whitespace on blur (defaults to false)
 * @param props.rest - Additional FormControlProps from React Bootstrap
 * @returns A React functional component rendering a Form.Control
 */
export const ControlBase = (props: ControlBaseProps): React.ReactElement => {
  const {
    value,
    setValue,
    whiteSpaceFix = false,
    disabled = false,
    type = "string",
    forcePrefix = "",
    className = "",
    spellCheck = true,
    debounceMilliseconds = 500,
    allowCarriageReturn = false,
    ...rest
  } = props;

  const [currentValue, setCurrentValue] = useState(value);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Force an immediate update, bypassing debounce.
   *
   * @param newValue - Optional new value to set before forcing the update
   */
  const forceUpdate = (newValue?: string) => {
    // Clear any pending debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }

    // Use provided value or current value
    const valueToUpdate = newValue ?? currentValue;

    // Update parent if setValue is available
    /* istanbul ignore else */
    if (setValue) {
      setValue(valueToUpdate);
    }

    // Update local state if new value provided
    if (newValue !== undefined) {
      setCurrentValue(valueToUpdate);
    }
  };

  // Sync with parent value changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  // Handle debounced updates when currentValue changes
  useEffect(() => {
    // Only debounce if setValue exists and value differs from parent
    if (setValue && currentValue !== value) {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set up debounced update (0ms for immediate)
      debounceTimer.current = setTimeout(() => {
        setValue(currentValue);
        debounceTimer.current = null;
      }, debounceMilliseconds);
    }

    // Cleanup function to clear timer on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [currentValue, value, setValue, debounceMilliseconds]);

  return (
    <Form.Control
      {...rest}
      size="sm"
      value={currentValue}
      disabled={disabled || !setValue}
      type={type !== "textarea" ? type : undefined}
      as={type === "textarea" ? "textarea" : undefined}
      onKeyDown={(e) => {
        if (
          (e.key === "Enter" && !allowCarriageReturn) ||
          (e.key === "Enter" && e.shiftKey && allowCarriageReturn && type === "textarea")
        ) {
          e.preventDefault();
          e.stopPropagation();
          forceUpdate();
        }
      }}
      onChange={(e) => {
        e.preventDefault();
        e.stopPropagation();
        /* istanbul ignore else */
        if (e.currentTarget.value.startsWith(forcePrefix)) {
          setCurrentValue(e.currentTarget.value);
        }
      }}
      className={className}
      onBlur={(e) => {
        if (e.currentTarget.value !== value)
          forceUpdate(
            whiteSpaceFix ? valueFix(e.currentTarget.value, true) : e.currentTarget.value,
          );
      }}
      spellCheck={spellCheck}
      placeholder={props.placeholder}
      autoFocus={props.autoFocus}
    />
  );
};

ControlBase.displayName = "ControlBase";
